import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDialog } from "../../../contexts/DialogContext";
import { getTimeStamp } from "../../../utils/common";
import { useTranslation } from "react-i18next";
import useMainContentDimensions from "../../../utils/mainContentDimensions";
import EditDelete from "../../../components/EditDelete";
import { showErrorSnackbar } from "../../../redux/slices/snackbar";
import { useDispatch } from "react-redux";

import {
  Card,
  CardHeader,
  Breadcrumbs as MuiBreadcrumbs,
  Paper as MuiPaper,
  Button,
  Typography,
  CardContent,
  Box,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import ToolBar from "../../../components/Toolbar";
import useWindowDimensions from "../../../utils/windowDimensions";
import useScheduleUser from "../../../hooks/useScheduleUser";
import CancelSchedule from "./CancelSchedule";
import ScheduleUserFilter from "./ScheduleUserFilter";
import ImmediateRenewalSchedule from "./ImmediateRenewalSchedule";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

const breadCrump = () => {
  return (
    <Breadcrumbs aria-label="Breadcrumb" mt={2}>
      <Typography>User</Typography>
      <Typography>Schedule User</Typography>
      <Typography>List</Typography>
    </Breadcrumbs>
  );
};

function ScheduleUser(props) {
  const { state } = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { getAllScheduleUser, scheduleUsers, paging, isLoading, downloadFile } = useScheduleUser();

  const { totalNumberOfRecords, hasNextPage, pageNumber, pageSize } = paging;

  const currentPayload = {
    filters: [],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [
      {
        field: "createdAt",
        sort: "desc",
      },
    ],
  };

  const [filterResetFunc, setFilterResetFunc] = useState(
    () => () => console.log("Refresh initialized"),
  );

  const refreshPage = () => {
    setPayload(currentPayload);
    filterResetFunc();
    setFilterFormData(null);
    setPaginationModel({
      page: 0,
      pageSize: 100,
    });
  };

  const onPageChange = (newPaginationModel) => {
    console.log(newPaginationModel, "pagepage");

    const pagination = {
      pageNumber: newPaginationModel.page === 0 ? 1 : newPaginationModel.page + 1,
      pageSize: newPaginationModel.pageSize, // Update pageSize dynamically
    };

    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 100,
  });

  const paginationMetaRef = useRef();

  // Memoize to avoid flickering when the `hasNextPage` is `undefined` during refetch
  const paginationMeta = React.useMemo(() => {
    if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage };
    }
    return paginationMetaRef.current;
  }, [hasNextPage]);

  const [sortModel, setSortModel] = useState([
    {
      field: "updatedAt",
      sort: "desc",
    },
  ]);
  const { height } = useWindowDimensions();

  const { openDialog } = useDialog();
  const { width: mainContentWidth } = useMainContentDimensions();
  const [selectedRows, setSelectedRows] = useState([]);
  const [payload, setPayload] = useState(currentPayload);
  const [filterFormData, setFilterFormData] = useState(null);

  const openFilterDialog = () => {
    return openDialog(
      "schedule_user_filter",
      "Schedule User Filter",
      <ScheduleUserFilter
        payload={payload}
        setPayload={setPayload}
        setFilterResetFunc={setFilterResetFunc}
        filterFormData={filterFormData}
        setFilterFormData={setFilterFormData}
      />,
      "md",
    );
  };

  const fetchData = async () => {
    await getAllScheduleUser(payload);
  };

  console.log(selectedRows, "seleecttttttt");

  const clickSchedule = (id) => {
    return openDialog(
      "cancel_schedule",
      "Cancel Schedule",
      <CancelSchedule
        key={getTimeStamp()}
        userId={id}
        refreshPage={refreshPage}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />,
    );
  };

  const clickImmediateRenewal = () => {
    return openDialog(
      "immediate_renewal_schedule",
      "Immediate Renewal Schedule",
      <ImmediateRenewalSchedule
        key={getTimeStamp()}
        refreshPage={refreshPage}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />,
    );
  };

  useEffect(() => {
    fetchData();
  }, [payload]);

  const handleSortModelChange = (newModel) => {
    console.log(newModel, "newModel");
    if (newModel.length !== 0) {
      setSortModel(newModel);
      setPayload((prev) => ({ ...prev, sortFields: newModel }));
    }
  };

  const columns = [
    {
      field: "radUser",
      headerName: "User Name",
      valueGetter: (params) => params?.userName,
      width: 150,
    },
    {
      field: "plan",
      headerName: "Plan",
      valueGetter: (params) => params?.planName,
      width: 150,
    },
    {
      field: "rate",
      headerName: "Rate",
      valueGetter: (params) => params?.rateName,
      width: 150,
    },
    {
      field: "scheduleDateStr",
      headerName: "Schedule Date",
      width: 150,
    },
    {
      field: "scheduleStatus",
      headerName: "Status",
      width: 100,
    },
    {
      field: "scheduleType",
      headerName: "Schedule Type",
      width: 150,
    },
    {
      field: "scheduleBy",
      headerName: "Schedule By",
      width: 150,
    },
    {
      field: "createdAtStr",
      headerName: "Created On",
      width: 150,
    },
    {
      field: "renewedDateStr",
      headerName: "Renewed Date",
      width: 150,
    },
    {
      field: "tenantName",
      headerName: "Franchise Name",
      width: 150,
    },
  ];

  return (
    <Card sx={{ width: "100%" }}>
      <CardHeader
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // Stack on small screens
          justifyContent: "space-between",
          alignItems: "flex-start", // Adjust alignment as needed
        }}
        action={
          <ToolBar
            downloadFile={(type) => downloadFile(type, payload)}
            refreshPage={refreshPage}
            cancelSchedulePage={clickSchedule}
            immediateRenewalPage={clickImmediateRenewal}
            isImmediateRenewalDisabled={selectedRows.length === 0}
            openFilterDialog={openFilterDialog}
          />
        }
        title={
          <Typography variant="h6" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
            {t("schedule_user_list")}
          </Typography>
        }
        subheader={<Box sx={{ display: "flex", flexWrap: "wrap" }}>{breadCrump()}</Box>}
      />
      <CardContent sx={{ height: height - 180, display: "flex", flexDirection: "column" }}>
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <DataGrid
            columns={columns}
            rows={scheduleUsers}
            getRowId={(row) => row["scheduleUserId"]}
            rowCount={totalNumberOfRecords}
            loading={isLoading}
            pagination
            page={paging.pageNumber}
            pageSize={paging.pageSize}
            paginationMode="server"
            paginationMeta={paginationMeta}
            pageSizeOptions={[25, 50, 100]}
            checkboxSelection
            sortingMode="server"
            sortModel={sortModel}
            onPaginationModelChange={onPageChange}
            onSortModelChange={handleSortModelChange}
            paginationModel={paginationModel}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setSelectedRows(newRowSelectionModel);
            }}
            rowSelectionModel={selectedRows}
            isRowSelectable={(params) =>
              params.row.scheduleStatus == "PENDING" && params.row.renewalType != "ONLINE"
            }
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
            }
            sx={{
              "& .MuiDataGrid-row.even-row": {
                backgroundColor: "#f9f9f9", // Light gray for even rows
              },
              "& .MuiDataGrid-row.odd-row": {
                backgroundColor: "#ffffff", // White for odd rows
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

export default ScheduleUser;
